import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People as PeopleIcon,
  QuestionAnswer as QAIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface ClientConsultationTabProps {
  data: any;
  loading?: boolean;
  error?: string;
}

const ClientConsultationTab: React.FC<ClientConsultationTabProps> = ({
  data,
  loading = false,
  error,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography>Loading client consultation analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography color="text.secondary">No client consultation data available</Typography>
      </Box>
    );
  }

  // Mock data structure - replace with actual API response structure
  const mockData = {
    clientConsultation: {
      clientProfile: {
        buyerType: "First-time homebuyer",
        budget: "$400,000 - $500,000",
        timeline: "3-6 months",
        priorities: ["Location", "School district", "Move-in ready"],
        concerns: ["Financing", "Market timing", "Property condition"]
      },
      keyQuestions: [
        "What's your timeline for purchasing?",
        "How important is the school district rating?",
        "Are you open to properties that need updates?",
        "What's your preferred down payment amount?",
        "Do you have any specific neighborhood preferences?"
      ],
      preparationChecklist: [
        "Pre-approval letter from lender",
        "List of must-have features",
        "Budget breakdown including closing costs",
        "Questions about property history",
        "Inspection preferences and timeline"
      ],
      marketInsights: {
        currentTrends: "Seller's market with limited inventory",
        priceExpectations: "Properties selling at or above asking price",
        competitionLevel: "High - multiple offers common",
        negotiationLeverage: "Limited buyer leverage in current market"
      },
      recommendations: {
        immediate: [
          "Get pre-approved immediately",
          "Prepare to act quickly on properties",
          "Have earnest money ready"
        ],
        preparation: [
          "Research school districts thoroughly",
          "Visit neighborhoods at different times",
          "Understand local market conditions"
        ],
        strategy: [
          "Consider properties slightly below budget",
          "Be prepared for multiple offer situations",
          "Have backup properties in mind"
        ]
      }
    },
    confidence: 88,
    dataQuality: "high"
  };

  const consultationData = data.clientConsultation || mockData.clientConsultation;

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PeopleIcon color="primary" />
        Client Consultation Preparation
      </Typography>

      {/* Client Profile */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Client Profile
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>Buyer Type:</strong> {consultationData.clientProfile?.buyerType}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>Budget Range:</strong> {consultationData.clientProfile?.budget}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>Timeline:</strong> {consultationData.clientProfile?.timeline}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>Top Priorities:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {consultationData.clientProfile?.priorities?.map((priority: string, index: number) => (
                  <Chip
                    key={index}
                    label={priority}
                    size="small"
                    color="primary"
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Key Questions */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <QAIcon color="info" />
                Key Questions to Ask
              </Typography>
              <List dense>
                {consultationData.keyQuestions?.map((question: string, index: number) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'info.main',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={question}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon color="success" />
                Preparation Checklist
              </Typography>
              <List dense>
                {consultationData.preparationChecklist?.map((item: string, index: number) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Market Insights */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingIcon color="warning" />
            Market Insights for Client
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>Current Market:</strong> {consultationData.marketInsights?.currentTrends}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>Price Expectations:</strong> {consultationData.marketInsights?.priceExpectations}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <strong>Competition Level:</strong> {consultationData.marketInsights?.competitionLevel}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Negotiation Leverage:</strong> {consultationData.marketInsights?.negotiationLeverage}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="secondary" />
            Strategic Recommendations
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'error.main' }}>
                Immediate Actions
              </Typography>
              <List dense>
                {consultationData.recommendations?.immediate?.map((rec: string, index: number) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'error.main',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={rec}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'warning.main' }}>
                Preparation Steps
              </Typography>
              <List dense>
                {consultationData.recommendations?.preparation?.map((rec: string, index: number) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'warning.main',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={rec}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'success.main' }}>
                Strategic Approach
              </Typography>
              <List dense>
                {consultationData.recommendations?.strategy?.map((rec: string, index: number) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={rec}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Data Quality Indicator */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Chip
          label={`${data.confidence || mockData.confidence}% Confidence`}
          color="success"
          variant="outlined"
          sx={{ mr: 1 }}
        />
        <Chip
          label={`${data.dataQuality || mockData.dataQuality} Quality Data`}
          color="info"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};

export default ClientConsultationTab;
